import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DataTable,
  Dropzone,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Panel,
  Pill,
  SearchInput,
  SegmentedControl,
  SkeletonTable,
} from '@bcecloud/portal-ui';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useUrlSearch } from '@/lib/use-list-query-state';
import { useTableSort } from '@/lib/use-table-sort';
import { errorMessage } from '@/lib/api';
import {
  INVOICE_ACCEPT,
  INVOICE_STATUS_LABELS,
  INVOICE_STATUS_TONES,
  READING_POLL_MS,
  invoicePath,
  isReading,
  lineLabel,
  listHeldLines,
  listInvoices,
  uploadInvoice,
  type InvoiceDocument,
} from '@/lib/invoices';
import { formatMoney, formatShortDate } from '@/proposals/lib/money';

export const INVOICES_QUERY_KEY = ['invoices'] as const;

/** The list's two views: invoices, and lines held for later across them. */
const VIEW = { invoices: 'invoices', held: 'held' } as const;
type View = (typeof VIEW)[keyof typeof VIEW];
const VIEW_PARAM = 'view';

/** Lines held for later, across invoices, newest first. */
function HeldLines(): React.ReactElement {
  const held = useQuery({ queryKey: [...INVOICES_QUERY_KEY, 'held'], queryFn: ({ signal }) => listHeldLines(signal) });
  if (held.isPending) return <SkeletonTable />;
  if (held.isError) return <ErrorNotice title="Held lines unavailable" message={errorMessage(held.error)} />;
  if (held.data.length === 0) {
    return (
      <Panel>
        <EmptyState
          title="Nothing held"
          message="Lines you hold for later (to ask a vendor, or wait for a contract) wait here until you bring them back."
        />
      </Panel>
    );
  }
  return (
    <DataTable headers={[{ label: 'Invoice' }, { label: 'Line' }, { label: 'Amount' }, { label: 'Why it is held' }]} rightAlignedHeaders={[2]}>
      {held.data.map((line) => (
        <tr key={line.id} className="border-t border-portal-line align-middle">
          <td className="p-3 text-portal-small">
            <Link to={invoicePath(line.documentId)} className="font-bold text-primary hover:underline">
              {line.vendorName ?? 'Invoice'}
            </Link>
            {line.invoiceNumber ? <span className="block text-portal-micro text-portal-muted">#{line.invoiceNumber}</span> : null}
          </td>
          <td className="p-3 text-portal-small">
            {line.description}
            <span className="block text-portal-micro text-portal-muted">Line {lineLabel(line)}</span>
          </td>
          <td className="p-3 text-portal-small text-right whitespace-nowrap">{formatMoney(line.amount)}</td>
          <td className="p-3 text-portal-small text-portal-muted">{line.holdReason ?? 'No reason given'}</td>
        </tr>
      ))}
    </DataTable>
  );
}

const INVOICE_SORT_FIELDS = {
  vendor: 'vendor',
  number: 'invoiceNumber',
  date: 'invoiceDate',
  total: 'total',
  status: 'statusLabel',
  added: 'createdAt',
} as const;
const INVOICE_SORT_KEYS = Object.values(INVOICE_SORT_FIELDS);
const DEFAULT_SORT = { key: INVOICE_SORT_FIELDS.added, direction: 'desc' as const };

interface InvoiceTableRow extends InvoiceDocument {
  readonly vendor: string;
  readonly statusLabel: string;
}

const SEARCH_FIELDS: ReadonlyArray<keyof InvoiceTableRow> = ['vendor', 'invoiceNumber', 'fileName', 'statusLabel'];

/** The second line under the vendor: the file and how many lines were read; nothing until a vendor is known (the file name is the title then). */
function rowDetail(row: InvoiceDocument): string | null {
  if (!row.vendorName) return null;
  const lines = row.lineCount === 1 ? '1 line' : `${row.lineCount} lines`;
  return row.lineCount > 0 ? `${row.fileName} · ${lines}` : row.fileName;
}

function toTableRow(document: InvoiceDocument): InvoiceTableRow {
  return { ...document, vendor: document.vendorName ?? '', statusLabel: INVOICE_STATUS_LABELS[document.status] };
}

/**
 * Tools → Invoices: drop a vendor invoice and it is read into its lines, ready
 * for review. Gated by the `process_invoices` permission at the route.
 * Plan: ~/projects/bce/plans/invoice-intake-plan.md.
 *
 * @returns The invoices list.
 */
export function InvoicesPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const { sort, onSort } = useTableSort(INVOICE_SORT_KEYS, DEFAULT_SORT);
  const { search, setSearch } = useUrlSearch();
  const [params, setParams] = useSearchParams();
  const view: View = params.get(VIEW_PARAM) === VIEW.held ? VIEW.held : VIEW.invoices;

  const invoices = useQuery({
    queryKey: INVOICES_QUERY_KEY,
    queryFn: ({ signal }) => listInvoices(signal),
    refetchInterval: (query) => (isReading(query.state.data ?? []) ? READING_POLL_MS : false),
  });

  const upload = useMutation({
    mutationFn: uploadInvoice,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: INVOICES_QUERY_KEY }),
  });

  const allRows = useMemo(() => (invoices.data ?? []).map(toTableRow), [invoices.data]);
  const rows = useMemo(() => sortRows(filterRows(allRows, search, SEARCH_FIELDS), sort), [allRows, search, sort]);

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="TOOLS"
        title="Invoices"
        subtitle="Drop a vendor invoice and its charges are read line by line, ready to post to each event's expense statement."
      />

      <Dropzone
        label="Drop a vendor invoice"
        hint="A PDF, or a photo of a paper invoice"
        accept={INVOICE_ACCEPT}
        uploading={upload.isPending}
        onFile={(file) => upload.mutate(file)}
        className="mb-4"
      />
      {upload.isError ? (
        <div className="mb-4">
          <ErrorNotice title="That invoice didn't upload" message={errorMessage(upload.error)} />
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SegmentedControl<View>
          label="Show"
          value={view}
          onChange={(next) => {
            const nextParams = new URLSearchParams(params);
            nextParams.set(VIEW_PARAM, next);
            setParams(nextParams, { replace: true });
          }}
          options={[
            { key: VIEW.invoices, label: 'Invoices' },
            { key: VIEW.held, label: 'Held lines' },
          ]}
        />
        {view === VIEW.invoices ? (
          <SearchInput
            label="Search invoices"
            placeholder="Vendor, invoice # or file name"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full max-w-sm"
          />
        ) : null}
      </div>

      {view === VIEW.held ? (
        <HeldLines />
      ) : invoices.isPending ? (
        <SkeletonTable />
      ) : invoices.isError ? (
        <ErrorNotice title="Invoices unavailable" message={errorMessage(invoices.error)} />
      ) : rows.length === 0 ? (
        <Panel>
          {allRows.length > 0 ? (
            <EmptyState title="No invoices match" message="Try a different search." />
          ) : (
            <EmptyState
              title="No invoices yet"
              message="Vendor invoices you drop above show up here, with every charge read and ready to review."
            />
          )}
        </Panel>
      ) : (
        <DataTable
          headers={[
            { label: 'Vendor', sortKey: INVOICE_SORT_FIELDS.vendor },
            { label: 'Invoice #', sortKey: INVOICE_SORT_FIELDS.number },
            { label: 'Date', sortKey: INVOICE_SORT_FIELDS.date },
            { label: 'Total', sortKey: INVOICE_SORT_FIELDS.total },
            { label: 'Status', sortKey: INVOICE_SORT_FIELDS.status },
            { label: 'Added', sortKey: INVOICE_SORT_FIELDS.added },
          ]}
          rightAlignedHeaders={[3]}
          sort={sort}
          onSort={onSort}
        >
          {rows.map((row) => (
            <tr key={row.id} className="border-t border-portal-line align-middle">
              <td className="p-3 text-portal-small">
                <Link to={invoicePath(row.id)} className="font-bold text-primary hover:underline">
                  {row.vendorName ?? row.fileName}
                </Link>
                {rowDetail(row) ? <span className="block text-portal-micro text-portal-muted">{rowDetail(row)}</span> : null}
              </td>
              <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">{row.invoiceNumber ?? '—'}</td>
              <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">{formatShortDate(row.invoiceDate)}</td>
              <td className="p-3 text-portal-small text-right whitespace-nowrap">
                {row.total === null ? '—' : formatMoney(row.total)}
              </td>
              <td className="p-3 text-portal-small whitespace-nowrap">
                <Pill tone={INVOICE_STATUS_TONES[row.status]}>{row.statusLabel}</Pill>
              </td>
              <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">{formatShortDate(row.createdAt)}</td>
            </tr>
          ))}
        </DataTable>
      )}
    </section>
  );
}
